import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { X } from "lucide-react";
import { useAnnouncements, type Lang } from "@/lib/announcements";
import { useLang } from "@/lib/i18n";

/**
 * Changelog 面板：渲染全部公告为更新时间线。
 * markdown 渲染器全部复用官网设计 token（brand / muted / card / mono），与官网无缝统一。
 * 信息层级：日期徽标 → 条目标题 → 章节 kicker（新增/优化/修复）→ 要点。
 */

// `###`（标准 changelog 分区：新增/优化/修复）→ 压暗的小号大写 kicker，与行内加粗拉开层级
const sectionKicker = (p: any) => {
  const { node, ...rest } = p;
  return (
    <div
      className="mt-5 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/45 first:mt-0"
      {...rest}
    />
  );
};

const mdComponents = {
  h1: sectionKicker,
  h2: sectionKicker,
  h3: sectionKicker,
  h4: sectionKicker,
  p: ({ node, ...p }: any) => <p className="my-2 text-sm leading-relaxed text-foreground/70" {...p} />,
  ul: ({ node, ...p }: any) => <ul className="my-2 space-y-1.5" {...p} />,
  li: ({ node, children }: any) => (
    <li className="relative pl-5 text-sm leading-relaxed text-foreground/80">
      <span className="absolute left-0 top-[0.55em] h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-brand" />
      <span>{children}</span>
    </li>
  ),
  strong: ({ node, ...p }: any) => <strong className="font-semibold text-foreground" {...p} />,
  a: ({ node, ...p }: any) => (
    <a className="text-brand underline-offset-2 hover:underline" target="_blank" rel="noopener noreferrer" {...p} />
  ),
  code: ({ node, ...p }: any) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.8em] text-foreground/90" {...p} />
  ),
  blockquote: ({ node, ...p }: any) => (
    <blockquote className="my-3 border-l-2 border-brand/50 pl-3 text-sm italic text-foreground/55" {...p} />
  ),
  hr: () => <hr className="my-4 border-border/70" />,
};

function fmtDate(iso: string, lang: Lang): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ChangelogPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lang } = useLang();
  const { announcements: items } = useAnnouncements();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="landing-v2">
      <div
        className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:items-center"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={lang === "zh" ? "更新日志" : "Changelog"}
      >
        <div
          className="glass-strong relative my-8 w-full max-w-2xl overflow-hidden rounded-2xl border border-border shadow-2xl animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 顶部品牌橙辉光，呼应官网氛围与公告栏 */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(90%_140%_at_50%_-50%,hsl(var(--brand)_/_0.12),transparent_70%)]" />

          {/* header */}
          <div className="relative flex items-center justify-between border-b border-border/60 px-5 py-4">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-brand shadow-[0_0_12px_2px_hsl(var(--brand)_/_0.6)]" />
              <h2 className="text-sm font-semibold tracking-wide text-foreground">
                {lang === "zh" ? "更新日志" : "What's new"}
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label={lang === "zh" ? "关闭" : "Close"}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* timeline */}
          <div className="relative max-h-[70vh] overflow-y-auto px-5 py-5">
            {items.map((a, i) => (
              <section key={a.id} className={i > 0 ? "mt-7 border-t border-border/50 pt-7" : ""}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="rounded-full bg-brand-soft px-2 py-0.5 font-mono text-[11px] text-brand">
                    {fmtDate(a.date, lang)}
                  </span>
                  {a.pinned && (
                    <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                      {lang === "zh" ? "最新" : "Latest"}
                    </span>
                  )}
                </div>
                <h3 className="mb-3 text-[15px] font-semibold leading-snug text-foreground">{a.title[lang]}</h3>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {a.bodyMd[lang]}
                </ReactMarkdown>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
